/**
 * Custom remark plugin to replace gatsby-remark-images
 * Generates responsive image HTML with SEO optimization
 * Works without Sharp dependency or external packages
 */
module.exports = ({ markdownAST }, pluginOptions = {}) => {
  const options = {
    maxWidth: 750,
    quality: 85,
    withWebp: true,
    loading: 'lazy',
    linkImagesToOriginal: false,
    wrapperStyle: 'margin-bottom: 1.0725rem;',
    ...pluginOptions,
  }

  // Manual tree walking to avoid unist-util-visit dependency
  function visitImages(node, index, parent) {
    if (node.type === 'image') {
      const { url, alt, title } = node

      // Skip external images (http/https URLs)
      if (!url.startsWith('http')) {
        // Generate responsive image HTML
        const responsiveImageHtml = generateResponsiveImageHtml(
          url,
          alt,
          title,
          options
        )

        // Replace the image node with raw HTML
        const htmlNode = {
          type: 'html',
          value: responsiveImageHtml,
        }

        if (parent && parent.children && typeof index === 'number') {
          parent.children[index] = htmlNode
        }
      }
    }

    // Recursively visit children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((child, childIndex) => {
        visitImages(child, childIndex, node)
      })
    }
  }

  visitImages(markdownAST)
  return markdownAST
}

function generateResponsiveImageHtml(src, alt = '', title = '', options) {
  const {
    maxWidth,
    quality,
    loading,
    linkImagesToOriginal,
    wrapperStyle,
  } = options

  // Generate different sizes for responsive images
  const sizes = [320, 640, 960, 1280, maxWidth].filter(
    (size) => size <= maxWidth
  )
  const baseName = src.replace(/\.[^/.]+$/, '') // Remove extension
  const extension = src.split('.').pop()

  // Generate srcset for different sizes
  const srcsetWebp = sizes
    .map((size) => {
      return `${baseName}-${size}w.webp ${size}w`
    })
    .join(', ')

  const srcsetFallback = sizes
    .map((size) => {
      return `${baseName}-${size}w.${extension} ${size}w`
    })
    .join(', ')

  // Generate sizes attribute based on responsive breakpoints
  const sizesAttr = [
    '(max-width: 320px) 320px',
    '(max-width: 640px) 640px',
    '(max-width: 960px) 960px',
    `${maxWidth}px`,
  ].join(', ')

  // Create picture element with WebP support and fallback
  const pictureHtml = `
    <picture>
      <source 
        type="image/webp" 
        srcset="${srcsetWebp}" 
        sizes="${sizesAttr}"
      />
      <img 
        src="${src}" 
        srcset="${srcsetFallback}"
        sizes="${sizesAttr}"
        alt="${alt}" 
        ${title ? `title="${title}"` : ''}
        loading="${loading}"
        style="max-width: 100%; height: auto; display: block;"
        width="${maxWidth}"
        height="auto"
      />
    </picture>
  `.trim()

  // Wrap in container if specified
  if (wrapperStyle) {
    return `<div style="${wrapperStyle}">${pictureHtml}</div>`
  }

  // Optionally link to original image
  if (linkImagesToOriginal) {
    return `<a href="${src}" target="_blank" rel="noopener noreferrer">${pictureHtml}</a>`
  }

  return pictureHtml
}

/**
 * Generate multiple image sizes from original
 * This would typically be done at build time
 * For now, we'll create a simple fallback
 */
function generateImageVariants(originalPath, sizes) {
  // In a real implementation, you'd use a build process to:
  // 1. Read the original image
  // 2. Generate resized versions
  // 3. Convert to WebP format
  // 4. Save to static directory

  // For now, return the original path
  // This could be enhanced with a build step using imagemin
  return sizes.map((size) => ({
    width: size,
    path: originalPath, // Would be generated variants in real implementation
  }))
}
